import type { TrackingConsent } from '@transcend-io/airgap.js-types';

import { ConsentPreferenceValue, type ConsentPurposePreferenceInput } from '@/api';
import { NON_CONFIGURABLE_CONSENT_PURPOSES } from '@/constants';
import type { AirgapPurposeValue } from '@/types/transcend';

export type AirgapPurposes = Record<string, AirgapPurposeValue>;

const toPreferenceValue = (value: AirgapPurposeValue): ConsentPreferenceValue | null => {
  if (value === true || value === 'on') return ConsentPreferenceValue.ALLOWED;
  if (value === false || value === 'off') return ConsentPreferenceValue.DENIED;
  // typeof guards against a non-string value a future SDK release could report.
  if (typeof value === 'string' && value.startsWith('Auto')) return ConsentPreferenceValue.AUTO;

  return null;
};

/**
 * Maps Airgap purposes to the backend input. Unmappable values are dropped: the backend reads an
 * omitted purpose as "not available", but rejects the whole snapshot on an unknown value.
 */
export const toPreferenceInput = (purposes: AirgapPurposes): ConsentPurposePreferenceInput[] =>
  Object.entries(purposes)
    .filter(([purpose]) => !NON_CONFIGURABLE_CONSENT_PURPOSES.includes(purpose))
    .map(([purpose, value]) => ({ purpose, value: toPreferenceValue(value) }))
    .filter((entry): entry is ConsentPurposePreferenceInput => entry.value !== null);

/**
 * Maps stored preferences back to Airgap purposes. AUTO is left out: Airgap re-resolves it on the
 * spot, overwriting the restored timestamp.
 */
export const toAirgapPurposes = (preferences: ConsentPurposePreferenceInput[]): TrackingConsent =>
  preferences.reduce<TrackingConsent>((purposes, { purpose, value }) => {
    if (value === ConsentPreferenceValue.ALLOWED) purposes[purpose] = true;
    if (value === ConsentPreferenceValue.DENIED) purposes[purpose] = false;

    return purposes;
  }, {});

/** Order-independent identity of a preference map, compared the way the backend compares it. */
export const preferenceSignature = (preferences: ConsentPurposePreferenceInput[]): string =>
  preferences
    .map(({ purpose, value }) => `${purpose}=${value}`)
    .sort((a, b) => a.localeCompare(b))
    .join(',');
